import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Customer Document (matches backend CustomerDocument : BaseDocument) ---

export interface Customer {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  address: string;
  website: string;
  source: string;
  status: string;
  notes: string;
  assigned_to: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs (match backend CreateCustomerRequest / UpdateCustomerRequest) ---

export interface CreateCustomerRequest {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  industry?: string;
  address?: string;
  website?: string;
  source?: string;
  status?: string;
  notes?: string;
  assigned_to?: string;
  tags?: string[];
}

export interface UpdateCustomerRequest {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  industry?: string;
  address?: string;
  website?: string;
  source?: string;
  status?: string;
  notes?: string;
  assigned_to?: string;
  tags?: string[];
}

// --- Filter params for customer list ---

export interface CustomerFilters {
  status?: string;
  industry?: string;
  source?: string;
  assigned_to?: string;
}

// --- API functions ---

const BASE = '/customers';

export async function listCustomers(
  params: SearchParams,
  filters?: CustomerFilters
): Promise<PagedResponse<Customer>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.status) query.status = filters.status;
  if (filters?.industry) query.industry = filters.industry;
  if (filters?.source) query.source = filters.source;
  if (filters?.assigned_to) query.assigned_to = filters.assigned_to;

  const response = await apiGet<PagedResponse<Customer>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getCustomer(id: string): Promise<Customer> {
  const response = await apiGet<Customer>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Customer not found');
  }
  return response.data;
}

export async function createCustomer(data: CreateCustomerRequest): Promise<Customer> {
  const response = await apiPost<Customer>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create customer');
  }
  return response.data;
}

export async function updateCustomer(id: string, data: UpdateCustomerRequest): Promise<Customer> {
  const response = await apiPut<Customer>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update customer');
  }
  return response.data;
}

export async function deleteCustomer(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}
